<template>
    <v-container grid-list-md fluid>
        <v-row no-gutters class="d-flex align-center justify-center">
            <v-col cols="6">
                <v-text-field
                    v-model="search"
                    label="Recherche"
                    outlined
                    hide-details
                    density="compact"
                    clearable
                    append-inner-icon="$search"
                />
            </v-col>
        </v-row>

        <div :style="'max-height:' + maxHeight + ';' + 'overflow:auto'">
            <v-row
                no-gutters
                class="d-flex justify-center mt-4"
                v-for="item of filtreSearch()"
                :key="item.name"
            >
                <v-col cols="8">
                    <v-card class="default_card mt-0 mb-4 mx-0" outlined style="cursor: pointer">
                        <v-row no-gutters>
                            <!-- Section Avatar et Nom -->
                            <v-col
                                cols="2"
                                class="d-flex align-center pa-0"
                                v-if="!$vuetify.display.mobile"
                            >
                                <v-avatar color="secondary">
                                    <v-icon>$label</v-icon>
                                </v-avatar>
                            </v-col>

                            <v-col cols="8">
                                <div
                                    class="d-flex align-center primary--text justify-space-between"
                                >
                                    <span
                                        :class="$vuetify.display.mobile ? 'ml-2' : ''"
                                        style="font-weight: bold; font-size: 16pt"
                                    >
                                        {{
                                            item.displayName
                                                ? item.displayName
                                                : item.name
                                                  ? $t(`profiles.name.${item.name}`)
                                                  : "Profil sans nom"
                                        }}
                                    </span>
                                    <span class="tag mr-2" style="opacity: 0.6" v-if="item.profile">
                                        {{ getProfileName(item) }}
                                    </span>
                                </div>
                            </v-col>
                            <!-- Section Actions -->
                            <v-col cols="2">
                                <v-row no-gutters>
                                    <v-col cols="6" class="d-flex justify-center pa-0">
                                        <v-tooltip
                                            v-if="userHasPermissions(item)"
                                            location="bottom"
                                        >
                                            <template v-slot:activator="{ props }">
                                                <v-btn-icon
                                                    v-bind="props"
                                                    v-if="userHasPermissions(item)"
                                                    icon="$edit"
                                                    large
                                                    color="secondary"
                                                    @click="openProfile(item.id)"
                                                    class="mt-1"
                                                />
                                            </template>
                                            <span>Modifier</span>
                                        </v-tooltip>
                                        <v-tooltip v-else location="bottom">
                                            <template v-slot:activator="{ props }">
                                                <v-btn-icon
                                                    icon="$oss-eye"
                                                    large
                                                    color="secondary"
                                                    @click="
                                                        onClickProfileRulesDialog(item.id, item)
                                                    "
                                                    class="mt-1"
                                                />
                                            </template>
                                            <span>Voir</span>
                                        </v-tooltip>
                                        <v-tooltip location="bottom">
                                            <template v-slot:activator="{ props }">
                                                <v-btn-icon
                                                    v-bind="props"
                                                    icon="$fa-clone"
                                                    large
                                                    color="secondary"
                                                    @click="openDuplicateDialog(item)"
                                                    :disabled="getRightToDuplicate(item) == false"
                                                    class="mt-1"
                                                />
                                            </template>
                                            <span>Dupliquer</span>
                                        </v-tooltip>
                                    </v-col>
                                    <v-col cols="6" class="d-flex justify-center pa-0">
                                        <v-tooltip location="bottom">
                                            <template v-slot:activator="{ props }">
                                                <v-btn-trash
                                                    v-bind="props"
                                                    @click="openDeleteDialog(item.id)"
                                                    :disabled="!userHasPermissions(item)"
                                                    size="large"
                                                />
                                            </template>
                                            <span>Supprimer</span>
                                        </v-tooltip>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>
        </div>

        <oss-dialog
            v-model="duplicateDialog"
            title="Dupliquer ce profil ?"
            @valid="duplicateProfile()"
            :previousDisplay="false"
            width="60vw"
        >
            <template v-slot:content>
                &#xca;tes-vous sur de vouloir dupliquer ce profil ?
            </template>
        </oss-dialog>
        <oss-dialog
            v-model="deleteDialog"
            title="Supprimer ce profil ?"
            @valid="deleteProfile()"
            :previousDisplay="false"
            width="60vw"
        >
            &#xca;tes-vous sur de vouloir supprimer ce profil ?
        </oss-dialog>
        <oss-dialog
            :title="'Droits du profil - ' + profileName"
            v-model="showProfileRulesDialog"
            min-width="80vw"
        >
            <profile :profileId="shownProfileId" :isConsultation="true" />
        </oss-dialog>
    </v-container>
</template>

<script lang="ts">
import { User } from "../../store/store";
import { Apollo } from "@/decorators";
import { Component, Vue, toNative } from "vue-facing-decorator";

import PROFILES from "../../graphql/profiles/Profiles.gql";
import DELETE_PROFILE from "../../graphql/profiles/DeleteProfile.gql";
import DUPLICATE_PROFILE from "../../graphql/profiles/DuplicateProfile.gql";
import { getProfileName } from "../../services/profile";
import { getUserBigram } from "../../services/userServices";
import Profile from "../admin/Profile.vue";
import { ProfileType } from "../../../../types/index";
import { checkAccessRights } from "../../services/accessRightServices";

@Component({
    components: {
        Profile,
    },
})
export class ProfilesTable extends Vue {
    selected = "";
    search = "";
    deleteDialog = false;
    user: any;
    profileIdToDelete = "";
    showProfileRulesDialog: boolean = false;
    shownProfileId: string = "";
    profileName: string = "";
    /**
     * Show duplicate dialog of given profile (profileIdToDuplicate) when true
     */
    duplicateDialog = false;
    profileIdToDuplicate = "";
    profileToDuplicate: any;

    get currentUser(): User {
        return this.$store.state.user;
    }

    get isAuthorized() {
        const authorized = checkAccessRights(this.currentUser, "profile", "write");
        return authorized;
    }

    get maxHeight() {
        const innHeight = window.innerHeight;
        const vhHeight = (170 / innHeight) * 100;
        const maxHeight_vh = 100 - vhHeight;

        return maxHeight_vh + "vh";
    }

    headers = [{ text: "Nom", value: "name" }, { text: "Description" }, { text: "Modifier" }];

    @Apollo({
        query: PROFILES,
        variables() {
            return {
                companyId: this.currentUser?.company?.id || "",
            };
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data) {
                    console.log(data);
                    this.items = data.profiles.map((profile: any) => {
                        const title =
                            profile.displayName || this.$t(`profiles.name.${profile.name}`);
                        return {
                            ...profile,
                            title,
                        };
                    });
                }
            }
        },
    })
    items: Array<any> = [];

    /**
     * Opens/Close dialog on click
     * @param item profile passed by item in list
     */
    onClickProfileRulesDialog(id: string, item: any) {
        this.shownProfileId = id;
        this.profileName = item.displayName
            ? item.displayName
            : this.$t(`profiles.name.${item.name}`);
        this.showProfileRulesDialog = !this.showProfileRulesDialog;
    }

    openProfile(id: string) {
        this.$router.push({
            name: "profile-edition",
            params: {
                profileId: id,
            },
        });
    }

    /**
     * Decide if the current user has rights high enough to duplicate said profile
     * @param profile profile in list
     */
    getRightToDuplicate(profile): boolean {
        if (
            !this.isAuthorized ||
            this.currentUser.profile.restrictionLevel > profile.restrictionLevel ||
            profile.type === ProfileType.ROOT
        ) {
            return false;
        }
        return true;
    }

    /**
     * Clones a profile, and returns the success with a boolean.
     * Refreshes profile on end of function
     * @param id id of profile
     * @param name name of profile, translated if translation is found in fr.json
     */
    async duplicateProfile() {
        try {
            const id = this.profileIdToDuplicate;
            const item = this.profileToDuplicate;
            let nameParameter = item.displayName || item.name;
            let companyId = null;

            debugger;

            // Si traduction disponible, remplace le nom
            const translatedName = this.$t(`profiles.name.${item.name}`);
            if (translatedName) {
                nameParameter = translatedName;
            }

            if (this.currentUser?.company?.siren !== "844122648") {
                companyId = this.currentUser?.company?.id;
            } else {
                companyId = null;
            }

            // Effectue la mutation
            const result = await this.$apollo.mutate({
                mutation: DUPLICATE_PROFILE,
                variables: {
                    id: id,
                    name: nameParameter,
                    companyId: companyId,
                },
            });

            if (result.data.duplicateProfile) {
                this.duplicateDialog = false;
            } else {
                console.error("Error during profile duplication:", result.errors);
            }

            await this.$apollo.queries.items.refetch();
        } catch (error) {
            console.error("Error during profile duplication:", error);
        }
    }

    openDeleteDialog(id: any) {
        this.profileIdToDelete = id;
        this.deleteDialog = true;
    }

    /**
     *
     * @param item profile
     */
    openDuplicateDialog(item: any) {
        this.profileIdToDuplicate = item.id;
        this.profileToDuplicate = item;
        this.duplicateDialog = true;
    }

    userHasPermissions(item: any) {
        // debugger;

        // Empêcher la suppression / modification si l'utilisateur n'a pas les droits d'écriture
        if (!this.isAuthorized) {
            return false;
        }

        // Empêcher la suppression / modification si le niveau de restriction du profil de l'utilisateur est inférieur
        if (this.currentUser.profile.restrictionLevel > item.restrictionLevel) {
            return false;
        }

        // Empêcher la suppression / modification des profils globaux (companyId null) sauf pour OSS360
        if (this.currentUser?.isRoot) {
            return true;
        }

        // Permettre la suppression / modification uniquement si le profil et l'utilisateur partagent le même companyId
        return item?.company?.id === this.currentUser?.company?.id;
    }

    async deleteProfile() {
        await this.$apollo.mutate({
            mutation: DELETE_PROFILE,
            variables: {
                id: this.profileIdToDelete,
            },
        });
        this.deleteDialog = false;
        await this.$apollo.queries.items.refetch();
    }

    getProfileName(user) {
        if (user?.profile) {
            return getProfileName(this, user.profile);
        }
        return "XXXX";
    }

    getUserBigram(user) {
        return getUserBigram(user);
    }

    filtreSearch() {
        // debugger;

        let results = [];
        if (this.search) {
            results = this.items.filter((item) => {
                return (
                    item.displayName?.toLowerCase().includes(this.search.toLowerCase()) ||
                    item.name?.toLowerCase().includes(this.search.toLowerCase()) ||
                    item.title?.toLowerCase().includes(this.search.toLowerCase())
                );
            });
        } else {
            results = this.items;
        }
        return results;
    }
}

export default toNative(ProfilesTable);
</script>
